// Tree SHAPES for the AI ability builder. Because the store only allows
// adjacent-tier dependencies, a skill tree is strictly layered — so its visible
// "shape" is just the skills-per-tier count profile. A shape is therefore a
// generation/layout parameter: it re-bins the generated (or template) skills into
// a chosen per-tier count profile, and (for converging shapes) marks the lone
// top-tier skill as a capstone that grants a completion bonus.
//
// Shape is ENFORCED by `reshapeLevels` (not merely hinted to the model), so an AI
// that ignores the requested counts still yields the chosen silhouette.

import { assignSkillIdsAndDeps, normalizeSkillLevelName, SKILL_LEVEL_ORDER } from './skillTree';

export type TreeShapeId = 'auto' | 'arrow' | 'pyramid' | 'diamond' | 'funnel';

export interface TreeShape {
  id: TreeShapeId;
  name: string;
  tagline: string;
  /** Converges to a single top-tier skill → that skill is a capstone. */
  converges: boolean;
}

export const TREE_SHAPES: TreeShape[] = [
  { id: 'auto', name: 'Auto', tagline: 'Let the AI choose the natural shape', converges: false },
  { id: 'arrow', name: 'Arrow', tagline: 'Parallel competencies advancing together', converges: false },
  { id: 'pyramid', name: 'Pyramid', tagline: 'Broad fundamentals converging to one mastery', converges: true },
  { id: 'diamond', name: 'Diamond', tagline: 'Focused start, exploratory middle, single capstone', converges: true },
  { id: 'funnel', name: 'Funnel', tagline: 'One gateway unlocking many specializations', converges: false },
];

export function getTreeShape(id?: string): TreeShape {
  return TREE_SHAPES.find((s) => s.id === id) ?? TREE_SHAPES[0];
}

// Relative tier WIDTH at normalised position t (0 = base, 1 = top).
function shapeWeight(id: TreeShapeId, t: number): number {
  switch (id) {
    case 'arrow':
      return 1;
    case 'pyramid':
      return 1 - 0.85 * t; // wide base → narrow top
    case 'funnel':
      return 0.15 + 0.85 * t; // narrow base → wide top
    case 'diamond':
      return 1 - Math.abs(0.5 - t) * 1.7; // peak in the middle
    default:
      return 1;
  }
}

/**
 * Apportion `total` skills across `tiers` tiers following a shape's width profile.
 * Every tier gets at least 1; the result always sums to `total` (clamped up to
 * `tiers` if there are fewer skills than tiers). Endpoints are forced so the
 * silhouette reads clearly: converging shapes end in a single top skill (capstone);
 * the funnel starts from a single base skill.
 */
export function distributeByShape(id: TreeShapeId, tiers: number, total: number): number[] {
  tiers = Math.max(1, Math.floor(tiers));
  total = Math.max(tiers, Math.floor(total)); // guarantee ≥1 per tier
  if (tiers === 1) return [total];

  const weights: number[] = [];
  for (let i = 0; i < tiers; i++) {
    weights.push(Math.max(0.05, shapeWeight(id, i / (tiers - 1))));
  }
  const wSum = weights.reduce((a, b) => a + b, 0);

  // Each tier starts at 1; apportion the remainder by largest-remainder method.
  const counts = new Array(tiers).fill(1);
  let remaining = total - tiers;
  const ideal = weights.map((w) => (w / wSum) * remaining);
  const floors = ideal.map((v) => Math.floor(v));
  for (let i = 0; i < tiers; i++) counts[i] += floors[i];
  remaining -= floors.reduce((a, b) => a + b, 0);
  const byFrac = ideal
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < remaining; k++) counts[byFrac[k % tiers].i]++;

  const converges = id === 'pyramid' || id === 'diamond';
  if (converges && counts[tiers - 1] > 1) {
    counts[0] += counts[tiers - 1] - 1; // fold the top down to the base
    counts[tiers - 1] = 1;
  }
  if (id === 'funnel' && counts[0] > 1) {
    counts[tiers - 1] += counts[0] - 1; // fold the base up to the top
    counts[0] = 1;
  }
  return counts;
}

/** Mark the lone top-tier skill of a converging shape as the capstone. */
function markCapstone(levels: any[], shape: TreeShape): any[] {
  if (!shape.converges || levels.length === 0) return levels;
  const top = levels[levels.length - 1];
  if (top.skills.length === 1) {
    top.skills = [{ ...top.skills[0], isCapstone: true }];
  }
  return levels;
}

/**
 * Re-shape grouped levels into the chosen shape, then assign ids + adjacent-tier
 * dependencies. 'auto' keeps the authored grouping (just ids + deps). For a named
 * shape the ordered skills are re-binned into the shape's per-tier counts while the
 * existing tier-NAME sequence is preserved, so foundation→mastery labels stay sane.
 */
export function reshapeLevels(levels: any[], shapeId?: string): any[] {
  const shape = getTreeShape(shapeId);

  const sorted = (Array.isArray(levels) ? levels : [])
    .map((l) => ({ level: normalizeSkillLevelName(l?.level), skills: Array.isArray(l?.skills) ? l.skills : [] }))
    .filter((g) => g.skills.length > 0)
    .sort((a, b) => SKILL_LEVEL_ORDER.indexOf(a.level as any) - SKILL_LEVEL_ORDER.indexOf(b.level as any));

  if (shape.id === 'auto' || sorted.length === 0) {
    return markCapstone(assignSkillIdsAndDeps(levels), shape);
  }

  const names = sorted.map((g) => g.level);
  const flat = sorted.flatMap((g) => g.skills);
  const counts = distributeByShape(shape.id, names.length, flat.length);

  const regrouped: any[] = [];
  let idx = 0;
  for (let i = 0; i < names.length; i++) {
    regrouped.push({ level: names[i], skills: flat.slice(idx, idx + counts[i]) });
    idx += counts[i];
  }
  if (idx < flat.length) regrouped[regrouped.length - 1].skills.push(...flat.slice(idx));

  return markCapstone(assignSkillIdsAndDeps(regrouped), shape);
}
